// 3rd Party
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components
import { FlexRow, H2, P } from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';
import { requestClinicPerformanceBadges } from 'connect/actions';
import { clinicIsPremium } from 'utils/helpers';

import { SuperPracticeBadgeSVG } from '../../../../assets/icons';
import AcceptingNewPatientSvg from '../../../common/components/svgs/AcceptingNewPatientSvg';
import CalendarSvg from '../../../common/components/svgs/CalendarSvg';
import DentalAnxietySvg from '../../../common/components/svgs/DentalAnxietySvg';
import MoonSvg from '../../../common/components/svgs/MoonSvg';
import PatientsChoiceSvg from '../../../common/components/svgs/PatientsChoiceSvg';
import ReliableResponderSvg from '../../../common/components/svgs/ReliableResponderSvg';
import RibbonSvg from '../../../common/components/svgs/RibbonSvg';
import ScheduleMasterSvg from '../../../common/components/svgs/ScheduleMasterSvg';
import SeeYouSoonSvg from '../../../common/components/svgs/SeeYouSoonSvg';
import SpeedyResponderSvg from '../../../common/components/svgs/SpeedyResponderSvg';
import SunriseSvg from '../../../common/components/svgs/SunriseSvg';

// Other

import { SuperPracticeBadge } from '../../../results/components/ResultCard/styled-components';
import {
  selectDataClinicPerformanceBadges,
  selectDataClinicSuperPracticeBadges,
  selectDataMatchOther,
} from '../../connect/selectors';
import { PROFILE_INSIGHT_TYPES } from '../../constants/constants';
import { getInsights } from '../../helpers/profile-need-to-know-helpers';
import {
  ProfileSubsectionBody,
  ProfileSubsectionHeader,
  ProfileSubsectionTitle,
} from '../../profile-styled-components';
import InsurancePaymentNeedToKnow from '../InsurancePaymentNeedToKnow/InsurancePaymentNeedToKnow';
import LanguagesNeedToKnow from '../LanguageNeedToKnow/LanguagesNeedToKnow';
import ServicesNeedToKnow from '../ServicesNeedToKnow/ServicesNeedToKnow';

import {
  Block,
  IconCircle,
  Info,
  SuperPracticeBlock,
  SuperPracticeText,
} from './styled-components';

const getInsightIcon = kind => {
  switch (kind) {
    case PROFILE_INSIGHT_TYPES.dentalAnxietyFriendly:
      return <DentalAnxietySvg height="28px" />;
    case PROFILE_INSIGHT_TYPES.earlyAvailability:
      return <SunriseSvg height="28px" />;
    case PROFILE_INSIGHT_TYPES.eveningAvailability:
      return <MoonSvg height="28px" />;
    case PROFILE_INSIGHT_TYPES.highlyExperienced:
      return <RibbonSvg height="28px" />;
    case PROFILE_INSIGHT_TYPES.newPatients:
      return <AcceptingNewPatientSvg height="28px" />;
    case PROFILE_INSIGHT_TYPES.seeYouSoon:
      return <SeeYouSoonSvg height="24px" />;
    case PROFILE_INSIGHT_TYPES.weekendAvailability:
      return <CalendarSvg height="28px" />;
    case PROFILE_INSIGHT_TYPES.firstResponseTimeHours:
      return <SpeedyResponderSvg height="28px" />;
    case PROFILE_INSIGHT_TYPES.responseRate:
      return <ReliableResponderSvg height="28px" />;
    case PROFILE_INSIGHT_TYPES.scheduleRate:
      return <ScheduleMasterSvg height="28px" />;
    case PROFILE_INSIGHT_TYPES.patientsChoice:
      return <PatientsChoiceSvg height="28px" />;
    default:
      return undefined;
  }
};

// Component
const ProfileNeedToKnow = ({ clinic, layout }) => {
  const dispatch = useDispatch();
  const {
    hasInsurance,
    insurance,
    dentalAnxiety,
    reasonRaw,
    specificAvailabilities,
    subReasonIds,
  } = useSelector(selectDataMatchOther) || {};
  const reasonRawExists =
    reasonRaw?.overall && !!Object.entries(reasonRaw.overall).length;
  const performanceBadges = useSelector(selectDataClinicPerformanceBadges);
  useEffect(() => {
    dispatch(requestClinicPerformanceBadges(clinic.id));
  }, [clinic]);

  const superPracticeBadges = useSelector(selectDataClinicSuperPracticeBadges);

  const insights = getInsights(
    clinic,
    specificAvailabilities,
    dentalAnxiety,
    performanceBadges,
    superPracticeBadges,
  );
  const { providers } = clinic;
  const primarySpecialty = _.get(clinic, 'primarySpecialty.id');

  return (
    <section>
      <ProfileSubsectionHeader>
        <ProfileSubsectionTitle>What you need to know</ProfileSubsectionTitle>
      </ProfileSubsectionHeader>
      <ProfileSubsectionBody>
        {(clinic.isSuperPractice || clinicIsPremium(clinic)) 
          && (
            <SuperPracticeBlock>
              <FlexRow align="center" gap="10px">
                <SuperPracticeBadge>
                  <SuperPracticeBadgeSVG />
                </SuperPracticeBadge>
                <SuperPracticeText>
                  <h3>Super Practice</h3>
                  <p>
                    Super Practices are highly rated and committed to providing a
                    great experience for new patients.
                  </p>
                </SuperPracticeText>
              </FlexRow>
            </SuperPracticeBlock>
          )
        }
        {insights && insights.length !== 0 
          && (
            <Block>
              <h3>Preferences & Highlights</h3>
              <FlexRow flexWrap="wrap" as="ul" padding="0">
                {insights.map(insight => (
                  <Info
                    key={insight.title}
                    margin="20px 0 0 15px"
                    width="47%"
                    as="li"
                  >
                    <div>
                      <IconCircle
                        background={insight.color}
                        height="50px"
                        margin="0 20px 0 0"
                        width="50px"
                      >
                        {getInsightIcon(insight.kind)}
                      </IconCircle>
                    </div>
                    <div>
                      <H2 margin="0">{insight.title}</H2>
                      <P color={colors.slate}>{insight.text}</P>
                    </div>
                  </Info>
                ))}
              </FlexRow>
            </Block>
          )
        }
        <Block>
          {/* If reasonRaw.overall does not exist, change text
          this if for users coming from SEO or the new funnel */}
          <h3>
            {reasonRawExists ? 'Your Selected Services' : 'Services Offered'}
          </h3>
          <ServicesNeedToKnow
            layout={layout}
            primarySpecialty={primarySpecialty}
            providers={providers}
            reasonExists={reasonRawExists}
            subReasonIds={subReasonIds}
            visitReason={reasonRaw}
          />
        </Block>
        <Block>
          <h3>Insurance & Payment</h3>
          <InsurancePaymentNeedToKnow
            clinic={clinic}
            hasInsurance={hasInsurance}
            insurance={insurance}
          />
        </Block>
        {_.get(providers, '[0].doctor.languages') 
          && (
            <Block>
              <h3>Languages Spoken</h3>
              <LanguagesNeedToKnow providers={providers} />
            </Block>
          )
        }
      </ProfileSubsectionBody>
    </section>
  );
};

ProfileNeedToKnow.propTypes = {
  clinic: PropTypes.object,
  layout: PropTypes.string,
};

export default ProfileNeedToKnow;
